"use client";

import { useMemo } from "react";
import { Tag } from "@/components/ui/tag";

function GradientTextParser({ text }: { text: string }) {
  return useMemo(() => {
    if (!text) return null;
    const lines = text.split("\n");
    return lines.map((line, lineIndex) => {
      const parts = line.split(/(`[^`]*`)|(\*[^*]*\*)/g).filter(Boolean);
      return (
        <span key={lineIndex}>
          {lineIndex > 0 && <br />}
          {parts.map((part, index) => {
            if (part.startsWith("`") && part.endsWith("`")) {
              const inner = part.slice(1, -1);
              const isItalic = inner.startsWith("*") && inner.endsWith("*");
              const content = isItalic ? inner.slice(1, -1) : inner;
              return (
                <span
                  key={index}
                  className={`bg-[url('/images/footer_gradient-low-res.webp')] text-transparent bg-center bg-clip-text bg-cover bg-no-repeat${isItalic ? " italic" : ""}`}
                >
                  {content}
                </span>
              );
            }
            if (part.startsWith("*") && part.endsWith("*")) {
              return <em key={index}>{part.slice(1, -1)}</em>;
            }
            return part;
          })}
        </span>
      );
    });
  }, [text]);
}

type Card = {
  stepLabel?: string;
  title?: string;
  description?: string;
  _key?: string;
};

export interface ProblemCardsProps {
  tag?: string;
  heading?: string;
  cards?: Card[];
  paddingTop?: string;
  paddingBottom?: string;
}

export default function ProblemCards({
  tag,
  heading,
  cards,
  paddingTop = "none",
  paddingBottom = "none",
}: ProblemCardsProps) {
  const ptClass = `section-pt-${paddingTop ?? "none"}`;
  const pbClass = `section-pb-${paddingBottom ?? "none"}`;

  return (
    <section className={`w-full ${ptClass} ${pbClass}`}>
      <div className="container mx-auto px-4 xl:px-[54px]">
        {(tag || heading) && (
          <div className="text-center max-w-[600px] mx-auto mb-12">
            {tag && (
              <div className="flex justify-center mb-6">
                <Tag variant="content">{tag}</Tag>
              </div>
            )}
            {heading && (
              <h2
                className="text-white font-normal m-0 leading-[130%] text-[36px]"
                style={{ fontFamily: "var(--font-roobert)" }}
              >
                <GradientTextParser text={heading} />
              </h2>
            )}
          </div>
        )}

        {cards && cards.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {cards.map((card, i) => (
              <div
                key={card._key ?? i}
                className="rounded-lg p-4 lg:p-6 bg-grey-900"
              >
                {card.stepLabel && (
                  <p className="text-[12px] font-mono text-grey-550 tracking-widest uppercase mb-3 m-0">
                    {card.stepLabel}
                  </p>
                )}
                {card.title && (
                  <h3 className="text-[20px] font-normal leading-snug m-0 mb-2.5 text-white">
                    {card.title}
                  </h3>
                )}
                {card.description && (
                  <p className="text-base font-normal leading-[1.65] m-0 text-grey-400">
                    {card.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
